import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DomesticBuyerForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_no: '',
    pan_no: '',
    state: '',
    state_code: '',
    account_no: '',
    ifsc_code: '',
    bank_name: '',
    bank_address: '',
  });
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_no: '',
    pan_no: '',
    state: '',
    state_code: '',
    account_no: '',
    ifsc_code: '',
    bank_name: '',
    bank_address: '',
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const navigate = useNavigate();

  // Fetch domestic buyer data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching domestic buyer with ID:', id);
      const fetchDomesticBuyer = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/domestic-buyer-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch domestic buyer');
          }
          const result = await res.json();
          console.log('Fetched domestic buyer data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            const newFormData = {
              name: result.data.name ?? '',
              contact_person: result.data.contact_person ?? '',
              phone: result.data.phone ?? '',
              email: result.data.email ?? '',
              address: result.data.address ?? '',
              gst_no: result.data.gst_no ?? '',
              pan_no: result.data.pan_no ?? '',
              state: result.data.state ?? '',
              state_code: result.data.state_code ?? '',
              account_no: result.data.account_no ?? '',
              ifsc_code: result.data.ifsc_code ?? '',
              bank_name: result.data.bank_name ?? '',
              bank_address: result.data.bank_address ?? ''
            };
            console.log('Setting new form data:', newFormData);
            setFormData(newFormData);
            setOriginalFormData(newFormData); // Store original values for read-only check
          } else {
            toast.error('Domestic Buyer not found');
            navigate('/domestic-buyer');
          }
        } catch (error) {
          console.error('Error fetching domestic buyer:', error);
          toast.error('Failed to load domestic buyer data');
          navigate('/domestic-buyer');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchDomesticBuyer();
    }
  }, [id, isEditMode, navigate]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('FormData changed:', formData);
  }, [formData]);

  const FIELD_LIMITS = {
    name: 100,
    contact_person: 100,
    phone: 20,
    email: 120,
    address: 1000, // text, UI limit
    gst_no: 30,
    pan_no: 20,
    state: 100,
    state_code: 10,
    account_no: 50,
    ifsc_code: 20,
    bank_name: 100,
    bank_address: 1000, // text, UI limit
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace(/_/g, ' ')}`;
    }
    // Required field
    if (name === 'name' && (!value || value.trim() === '')) {
      error = 'Name is required';
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) toast.error(error);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Check if field is read-only (has existing value from database)
    const isReadOnly = (fieldName) => {
      // Only check if this is edit mode and the field had an original value
      if (!isEditMode) return false;
      
      // Get the original value from the database (stored separately)
      const originalValue = originalFormData[fieldName];
      return originalValue && originalValue.toString().trim() !== '';
    };
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    const matched = stateList.find(s => s.name === selectedState);
    setFormData(prev => ({
      ...prev,
      state: selectedState,
      state_code: matched ? matched.code : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validate all fields
    for (const key in formData) {
      if (FIELD_LIMITS[key]) {
        const error = validateField(key, formData[key]);
        if (error) {
          toast.error(error);
          setLoading(false);
          return;
        }
      }
    }
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/domestic-buyer-put/${id}`
        : `${API_BASE_URL}/api/masters/domestic-buyer-post`;
      
      console.log('=== SUBMITTING DOMESTIC BUYER DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', formData);
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'Domestic Buyer Updated Successfully!' : 'Domestic Buyer Registered Successfully!');
        if (!isEditMode) {
          setFormData({
            name: '', contact_person: '', phone: '', email: '', address: '', gst_no: '', pan_no: '', state: '', state_code: '', account_no: '', ifsc_code: '', bank_name: '', bank_address: '',
          });
        } else {
          navigate('/domestic-buyer');
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b pb-3 mb-5">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-medium text-gray-800">{isEditMode ? 'Edit Domestic Buyer' : 'Domestic Buyer Registration'}</h2>
              {isEditMode && (
                <div className="text-xs text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-3 py-1 text-xs rounded font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
            >
              <FaArrowLeft />
              Back
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.name} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" required autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Contact Person</label>
            <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.contact_person} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Phone</label>
            <input type="text" name="phone" value={formData.phone} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.phone} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.email} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Address</label>
            <textarea name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.address} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none resize-none" rows={2}></textarea>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">GST No</label>
            <input type="text" name="gst_no" value={formData.gst_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.gst_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">PAN No</label>
            <input type="text" name="pan_no" value={formData.pan_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.pan_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">State</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleStateChange}
              onBlur={handleBlur}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
              required
            >
              <option value="">-- Select State --</option>
              {stateList.map((s, i) => (
                <option key={i} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">State Code</label>
            <input
              type="text"
              name="state_code"
              value={formData.state_code}
              readOnly
              maxLength={FIELD_LIMITS.state_code}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Account No</label>
            <input type="text" name="account_no" value={formData.account_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.account_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">IFSC Code</label>
            <input type="text" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.ifsc_code} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Bank Name</label>
            <input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.bank_name} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Bank Address</label>
            <input type="text" name="bank_address" value={formData.bank_address} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.bank_address} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" className="px-4 py-2 text-xs bg-[#10a5e9] text-white font-normal rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60" style={{ minWidth: '110px' }} disabled={loading}>
            {loading ? 'Saving...' : (isEditMode ? 'Update Domestic Buyer' : 'Save Domestic Buyer')}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default DomesticBuyerForm;
